import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast, Toaster } from 'sonner';
import { Plus, Trash2, Calendar, Receipt } from 'lucide-react';
import Seo from '@/components/Seo';
import ProUpsell from '@/components/ProUpsell';

const gbp = (n) => `£${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUSES = {
  draft:     { label: 'Draft',     cls: 'bg-slate-200 text-slate-900' },
  sent:      { label: 'Sent',      cls: 'bg-blue-200 text-blue-900' },
  part_paid: { label: 'Part paid', cls: 'bg-amber-200 text-amber-900' },
  paid:      { label: 'Paid',      cls: 'bg-emerald-500 text-white' },
  void:      { label: 'Void',      cls: 'bg-rose-200 text-rose-900' },
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/invoices'); setInvoices(data || []); }
    catch { toast.error('Could not load invoices'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isPro) load(); else setLoading(false); }, [isPro]);

  if (user && !isPro) {
    return (
      <div>
        <Seo title="Invoices" noindex />
        <ProUpsell feature="Invoices" testIdSuffix="invoices" tier="pro" />
      </div>
    );
  }

  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/invoices', {
        title: 'New invoice', vat_rate: 20,
        items: [{ description: '', qty: 1, unit: 'job', unit_price: 0 }],
        payment_details: user?.payment_details || '',
      });
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not create invoice');
      setCreating(false);
    }
  };

  const del = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Delete this invoice?')) return;
    try { await api.delete(`/invoices/${id}`); setInvoices((v) => v.filter((x) => x.id !== id)); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const visible = filter === 'all' ? invoices : invoices.filter((i) => (i.status || 'draft') === filter);
  const outstanding = invoices.filter((i) => i.status !== 'paid' && i.status !== 'void').reduce((s, i) => s + (i.balance_due != null ? i.balance_due : i.total), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Invoices" noindex />
      <Toaster richColors position="top-right" />
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Invoices</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Invoices</h1>
          <p className="text-slate-600 mt-2">Send invoices to customers and track what&apos;s been paid.</p>
        </div>
        <button data-testid="invoice-new-btn" onClick={createNew} disabled={creating} className="tm-btn">
          <Plus size={16} /> {creating ? 'Creating…' : 'New invoice'}
        </button>
      </header>

      {invoices.length > 0 && (
        <div className="tm-card p-4 mb-6 flex items-center justify-between gap-3 bg-slate-50" data-testid="invoice-outstanding">
          <p className="text-xs uppercase tracking-widest text-slate-700 font-semibold">Outstanding balance</p>
          <p className="font-mono font-semibold text-2xl text-brand">{gbp(outstanding)}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        {['all', 'draft', 'sent', 'part_paid', 'paid'].map((s) => {
          const count = s === 'all' ? invoices.length : invoices.filter((i) => (i.status || 'draft') === s).length;
          const active = filter === s;
          const label = s === 'all' ? 'All' : STATUSES[s].label;
          return (
            <button key={s} data-testid={`invoice-filter-${s}`} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 border border-slate-800 font-display font-bold uppercase text-xs tracking-wider transition-colors ${active ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 hover:bg-slate-100'}`}>
              {label} <span className="font-mono ml-1">({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="uppercase tracking-widest text-slate-500 font-semibold">Loading…</p>
      ) : invoices.length === 0 ? (
        <div className="tm-card p-10 text-center">
          <Receipt className="mx-auto text-brand" size={48} />
          <h3 className="font-display font-bold text-2xl uppercase tracking-tight mt-4">No invoices yet</h3>
          <p className="text-slate-600 mt-2">Create one from scratch, or turn an accepted quote into an invoice from your <Link to="/dashboard" className="text-brand underline">dashboard</Link>.</p>
          <button onClick={createNew} className="tm-btn mt-6 inline-flex"><Plus size={16} /> New invoice</button>
        </div>
      ) : visible.length === 0 ? (
        <p className="font-mono text-sm text-slate-500">No invoices match this filter.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {visible.map((inv) => {
            const st = STATUSES[inv.status || 'draft'];
            const date = inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB') : '';
            return (
              <Link key={inv.id} to={`/invoices/${inv.id}`} data-testid={`invoice-card-${inv.id}`} className="tm-card p-6 flex flex-col gap-3 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="tm-chip tm-chip-orange font-mono">{inv.invoice_number}</span>
                    <span className={`tm-chip ${st.cls}`} data-testid={`invoice-status-${inv.id}`}>{st.label}</span>
                  </div>
                  <button data-testid={`invoice-delete-${inv.id}`} onClick={(e) => del(inv.id, e)} className="text-slate-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={16} /></button>
                </div>
                <h3 className="font-display font-bold text-xl uppercase tracking-tight">{inv.title}</h3>
                {inv.customer_name && <p className="text-xs text-slate-600 uppercase tracking-wider font-semibold">For: {inv.customer_name}</p>}
                <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1 font-semibold"><Calendar size={12} /> {date}</p>
                <div className="tm-result mt-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-widest text-slate-300">Total</span>
                    <span className="font-mono font-semibold text-2xl text-brand">{gbp(inv.total)}</span>
                  </div>
                  {inv.amount_paid > 0 && inv.status !== 'paid' && (
                    <div className="flex items-baseline justify-between mt-1">
                      <span className="text-xs uppercase tracking-widest text-slate-300">Balance</span>
                      <span className="font-mono text-sm text-amber-300">{gbp(inv.balance_due)}</span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
